// ── LT-2 — /billing ──────────────────────────────────────────────────────────
// Owner-only in practice (the edge functions refuse anyone else), but the
// ROUTE is open: a sales user who follows a link lands here and is told who
// can pay, rather than being bounced to the dashboard as if the link were
// broken.
//
// The price is read from Stripe. No amount is hard-coded anywhere in this app,
// because the day it changes there must be exactly one place to change it.
import { useCallback, useEffect, useState } from "react";
import { getBillingState, getPrice, startCheckout, openPortal } from "../lib/billing";
import { useAuth } from "../auth/AuthProvider";
import { C } from "../customers/theme";

const card = { background: "#121821", border: `1px solid ${C.border}`, borderRadius: 12, padding: 24, marginBottom: 16 };
const btn = (variant = "primary") => ({
  padding: "10px 20px", borderRadius: 8, border: "none", fontSize: 14, fontWeight: 700, cursor: "pointer",
  ...(variant === "primary"
    ? { background: C.primary, color: "#04212b" }
    : { background: "transparent", color: "#94a3b8", border: "1px solid #334155" }),
});

function formatPrice(price) {
  if (!price || typeof price.amount !== "number") return null;
  const amount = (price.amount / 100).toLocaleString("en-IE", {
    style: "currency", currency: (price.currency || "eur").toUpperCase(),
  });
  return price.interval ? `${amount} per ${price.interval}` : amount;
}

export default function BillingScreen() {
  const { role, refreshBilling } = useAuth();
  const [billing, setBilling] = useState(null);
  const [price, setPrice] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const isOwner = role === "owner";

  const load = useCallback(async () => {
    try { setBilling(await getBillingState()); }
    catch (e) { setError(e.message); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { getPrice().then(setPrice); }, []);

  // ?checkout=success|cancel — Stripe's return. Read once, then removed from
  // the URL so a refresh does not re-toast, and a copied link does not carry
  // somebody else's "success".
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const outcome = params.get("checkout");
    if (!outcome) return;
    setToast(outcome === "success"
      ? "Thanks — you're subscribed. It can take a few seconds for this page to catch up."
      : "Checkout cancelled. Nothing has been charged.");
    window.history.replaceState(null, "", window.location.pathname);
    // The webhook may land a moment after the redirect, so re-read shortly
    // after as well as immediately. The state is the database's, not a guess.
    load();
    const t = setTimeout(() => { load(); if (refreshBilling) refreshBilling(); }, 3000);
    return () => clearTimeout(t);
  }, [load, refreshBilling]);

  const run = async (fn) => {
    setBusy(true); setError(null);
    try { window.location.href = await fn(); }
    catch (e) { setError(e.message); setBusy(false); }
  };

  const state = billing?.state;
  const days = billing?.daysLeft;

  return (
    <div style={{ maxWidth: 680 }}>
      {toast && (
        <div style={{ ...card, background: C.successDim, border: `1px solid ${C.success}`, padding: "12px 16px", fontSize: 13.5 }}>
          {toast}
        </div>
      )}

      <div style={card}>
        <h2 style={{ margin: "0 0 4px", fontSize: 20, fontWeight: 700, color: C.text }}>Subscription</h2>

        {!billing && !error && <div style={{ color: "#94a3b8", fontSize: 14 }}>Loading…</div>}

        {state === "trialing" && (
          <>
            <div style={{ fontSize: 15, color: C.text, marginTop: 10 }}>
              You're on your free trial{typeof days === "number" ? ` — ${days} ${days === 1 ? "day" : "days"} left` : ""}.
            </div>
            <div style={{ fontSize: 13.5, color: "#94a3b8", marginTop: 8, lineHeight: 1.65 }}>
              No card needed until the trial ends, and nothing happens automatically when it does — your books stay
              exactly where they are and you can still open, print and export everything. You just won't be able to
              add anything new until you subscribe.
            </div>
          </>
        )}

        {state === "active" && (
          <div style={{ fontSize: 15, color: C.text, marginTop: 10 }}>
            Your subscription is active. Thanks for using M².
            {billing?.currentPeriodEnd && (
              <div style={{ fontSize: 13.5, color: "#94a3b8", marginTop: 8 }}>
                Next renewal: {new Date(billing.currentPeriodEnd).toLocaleDateString("en-IE", { day: "numeric", month: "long", year: "numeric" })}.
              </div>
            )}
            {billing?.status === "past_due" && (
              <div style={{ fontSize: 13.5, color: C.warning, marginTop: 8 }}>
                Your last payment didn't go through. Update your card in the next few days to avoid interruption —
                everything keeps working in the meantime.
              </div>
            )}
          </div>
        )}

        {state === "read_only" && (
          <>
            <div style={{ fontSize: 15, color: C.text, marginTop: 10 }}>Your account is read-only.</div>
            <div style={{ fontSize: 13.5, color: "#94a3b8", marginTop: 8, lineHeight: 1.65 }}>
              Nothing has been deleted and nothing will be. Every invoice, expense and VAT return is still here to
              open, print and export. Subscribe to start adding new work again.
            </div>
          </>
        )}

        {price && (
          <div style={{ fontSize: 14, color: C.text, marginTop: 16, fontWeight: 600 }}>{formatPrice(price)}</div>
        )}

        {error && <div style={{ fontSize: 13.5, color: C.danger, marginTop: 14 }}>{error}</div>}

        {!isOwner && (
          <div style={{ fontSize: 13.5, color: "#94a3b8", marginTop: 16 }}>
            Only the owner of the business can change the subscription. Ask them to open this page.
          </div>
        )}

        {isOwner && (
          <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
            {state !== "active" && (
              <button style={btn()} disabled={busy} onClick={() => run(startCheckout)}>
                {busy ? "Opening…" : "Subscribe"}
              </button>
            )}
            {billing?.hasCustomer && (
              <button style={btn("secondary")} disabled={busy} onClick={() => run(openPortal)}>
                Manage billing
              </button>
            )}
          </div>
        )}
      </div>

      <div style={{ ...card, background: "transparent", border: `1px dashed ${C.border}` }}>
        <div style={{ fontSize: 13, color: "#64748b", lineHeight: 1.7 }}>
          Payments are handled by Stripe. We never see or store your card details. Cancel any time from
          <strong style={{ color: "#94a3b8" }}> Manage billing</strong> — your data stays yours either way.
        </div>
      </div>
    </div>
  );
}
